import { fmtWeekdayDayMonth } from './planner_dates.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import type { PlannerLeg } from './planner_models.js';
import type { SkirtBest } from './heat_savings.js';

/** O glifo de autoria da VOLL (duas faíscas, Figma 16/10): assina o que o agente
 *  afirma. Herda a cor da frase — a dica é verde, e o glifo vai com ela. */
function AuthorshipGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="14"
      height="14"
      fill="none"
      aria-hidden="true"
      className="flex-none"
    >
      <path
        fill="currentColor"
        d="M8.55801 3.6581C8.87985 2.78011 10.1198 2.78026 10.4418 3.6581L11.8774 7.52627C11.9779 7.80114 12.1992 8.01568 12.474 8.12295L16.3422 9.55849C17.22 9.8805 17.2202 11.1205 16.3422 11.4423L12.474 12.8769C12.1992 12.9774 11.9846 13.1987 11.8774 13.4735L10.4418 17.3417C10.1199 18.2197 8.87979 18.2199 8.55801 17.3417L7.12344 13.4735C7.02288 13.1987 6.80164 12.9841 6.52676 12.8769L2.6586 11.4423C1.78041 11.1205 1.78052 9.88039 2.6586 9.55849L6.52676 8.12295C6.80164 8.02238 7.01618 7.80114 7.12344 7.52627L8.55801 3.6581ZM15.5385 1.32021C15.6956 0.893264 16.2994 0.893262 16.4613 1.32021L16.9818 2.72841C17.0309 2.86088 17.1385 2.96935 17.2709 3.01845L18.6801 3.53896C19.1065 3.69626 19.1067 4.29978 18.6801 4.46181L17.2709 4.98134C17.1384 5.03043 17.0309 5.13889 16.9818 5.27138L16.4613 6.67959C16.3043 7.10658 15.7004 7.10658 15.5385 6.67959L15.018 5.27138C14.9689 5.13889 14.8614 5.03043 14.7289 4.98134L13.3197 4.46181C12.8931 4.30468 12.8933 3.70115 13.3197 3.53896L14.7289 3.01845C14.8614 2.96935 14.9689 2.86088 15.018 2.72841L15.5385 1.32021Z"
      />
    </svg>
  );
}

/** A altura de uma linha de `text-detail` (13/18): a 2ª linha do agente e a do
 *  lastro guardam o lugar mesmo vazias, para o bloco não pular quando a economia
 *  aparece num dia e não no outro (#5693). */
const DETAIL_LINE = 'block min-h-[18px] text-detail';

/** A 2ª linha do agente: a economia, ou a espera pela data. A espera é ESTÁTICA
 *  (texto em degradê, sem shimmer): shimmer é "carregando", e aqui quem falta
 *  agir é a pessoa. Sem 2ª linha (a escolha já é a mais econômica, ou a
 *  diferença não chega ao piso), o lugar fica vazio. */
function FollowLine({ follow }: { readonly follow: SkirtBest['follow'] }) {
  const tone = follow?.awaiting
    ? 'bg-linear-96 from-sand-12 from-48% to-[#dddddd] to-105% bg-clip-text font-semibold text-transparent'
    : 'font-semibold text-sand-12';
  return <span className={`${DETAIL_LINE} ${tone}`}>{follow?.text}</span>;
}

/** O esqueleto de UMA linha: a barra no meio da caixa da linha que ela ocupa,
 *  para a leitura não encolher nem esticar o bloco ao acabar (#5693). */
function ShimLine({ line, short = false }: { readonly line: string; readonly short?: boolean }) {
  return (
    <span className={`${line} items-center`} aria-hidden="true">
      <span className={short ? 'fpl-hem-shim is-short' : 'fpl-hem-shim'} />
    </span>
  );
}

/**
 * A saia do calendário: duas colunas, e a divisão é por AUTORIA. Elas crescem a
 * partir do conteúdo (`flex-auto`), não em metades fixas: no popover de produção
 * (~540px) as metades quebravam a dica do agente em duas linhas.
 *
 * À esquerda o que a PESSOA fez — o estado da escolha ("Escolha a data de ida",
 * "Ida Qua, 25 ago · escolha a volta", "Ter, 25 ago - Seg, 31 ago • 6 noites") — e,
 * embaixo, quantas datas a grade cobre. À direita o que o AGENTE afirma — qual é o
 * dia mais barato e, na 2ª linha, quanto a pessoa economiza voando nele (#5246).
 * O glifo assina a afirmação (R-voo-10).
 *
 * Ela substituiu o título/subtítulo em serif que o card tinha. Um marcador na
 * grade diz "este dia é especial"; só texto diz POR QUE — e a rota, que era
 * metade do título, já está escrita nos campos DE/PARA logo acima.
 *
 * Componente burro de propósito: quem calcula as frases é o card, que é quem tem
 * os picks e o bloco. Aqui só mora o arranjo.
 */
export function PlannerStatus({
  choice,
  loading,
  loadingLabel,
  best,
  meta,
  notice,
  sheet = false,
}: {
  /** O estado da escolha, sempre presente. */
  readonly choice: string;
  /** Heatmap em voo: as frases do agente viram esqueleto. Elas são TRABALHO dele
   *  (varrer a janela e escolher o dia) — aparecerem prontas junto com a cor faria
   *  o conselho parecer enfeite pré-calculado. */
  readonly loading: boolean;
  readonly loadingLabel: string;
  /** O dia mais barato e a 2ª linha. Ausente = não há heat a recomendar. */
  readonly best?: SkirtBest;
  /** "N datas analisadas". */
  readonly meta?: string;
  /** Rota resolvida e sem heat (SERP fora do ar): ocupa o lugar das frases do agente. */
  readonly notice?: string;
  /** Na FOLHA do celular o lastro e a 2ª linha do agente leem numa linha só,
   *  embaixo (`SheetDetailLine`, desenho da #5662). */
  readonly sheet?: boolean;
}) {
  // Na FOLHA do celular (#3446, as variantes `in-[.fpl-sheet]:`) a saia lê em UMA
  // coluna, alinhada à esquerda: numa coluna de telefone o agente à direita
  // descolava do resto do texto. As classes ficam literais para o Tailwind achá-las.
  return (
    <div className="fpl-hem flex min-h-[50px] items-center justify-between gap-4 in-[.fpl-sheet]:min-h-0 in-[.fpl-sheet]:flex-col in-[.fpl-sheet]:items-start in-[.fpl-sheet]:gap-1">
      <div className="flex min-w-0 flex-auto flex-col gap-2 in-[.fpl-sheet]:flex-none in-[.fpl-sheet]:gap-1">
        <span
          className="fpl-hem-choice text-ui text-sand-12 in-[.fpl-sheet]:text-muted-foreground"
          aria-live="polite"
        >
          {choice}
        </span>
        {/* O lastro do número fica do lado da pessoa, embaixo da escolha. `meta`
            é INDEPENDENTE de `best`: um bloco com dias de calor mas sem `cheapest`
            tem meta e não tem recomendação. Na leitura ele guarda a linha. */}
        {meta && !sheet && (
          <span className={`fpl-hem-meta ${DETAIL_LINE} text-sand-12`}>{loading ? '' : meta}</span>
        )}
      </div>
      <div
        className="fpl-hem-agent flex min-w-0 flex-auto flex-col items-end gap-2 text-right in-[.fpl-sheet]:flex-none in-[.fpl-sheet]:items-start in-[.fpl-sheet]:gap-1 in-[.fpl-sheet]:text-left"
        {...(loading ? { role: 'status', 'aria-label': loadingLabel } : {})}
      >
        <AgentBody loading={loading} notice={notice} best={best} sheet={sheet} />
      </div>
      {sheet && <SheetDetailLine meta={meta} best={best} loading={loading} />}
    </div>
  );
}

/** As frases do agente: o esqueleto na leitura, o aviso sem heat, ou o dia mais
 *  barato e a 2ª linha (que, na folha, desce para a `SheetDetailLine`). */
function AgentBody({
  loading,
  notice,
  best,
  sheet,
}: {
  readonly loading: boolean;
  readonly notice: string | undefined;
  readonly best: SkirtBest | undefined;
  readonly sheet: boolean;
}) {
  if (loading) {
    return (
      <>
        <ShimLine line="flex min-h-5" />
        {!sheet && <ShimLine line="flex min-h-[18px]" short />}
      </>
    );
  }
  if (notice) return <span className="text-ui font-normal text-sand-12">{notice}</span>;
  if (!best) return null;
  return (
    <>
      <span className="fpl-hem-best inline-flex items-center gap-1 text-ui text-success-strong">
        <AuthorshipGlyph />
        {best.day}
      </span>
      {!sheet && <FollowLine follow={best.follow} />}
    </>
  );
}

/** A 3ª linha da FOLHA do celular, no desenho da #5662: o lastro e a 2ª linha do
 *  agente numa linha só, em `muted` ("144 datas analisadas · voe neste dia e
 *  economize R$ 252"). Guarda a altura vazia, como no popover. */
function SheetDetailLine({
  meta,
  best,
  loading,
}: {
  readonly meta: string | undefined;
  readonly best: SkirtBest | undefined;
  readonly loading: boolean;
}) {
  if (!meta && !best) return null;
  if (loading) return <ShimLine line="flex min-h-[18px]" short />;
  const parts = [meta, best?.follow?.inline].filter(Boolean);
  return (
    <span className={`fpl-hem-detail ${DETAIL_LINE} text-muted-foreground`}>
      {parts.join(' · ')}
    </span>
  );
}

/**
 * A saia do MULTI: o mesmo par de frases do binário, nomeando o TRECHO em vez de
 * falar de ida e volta — no multi não há ida nem volta, há a perna sendo escolhida.
 *
 * Sem heat ela diz "sem tendência de preço", e é essa linha que impede a grade neutra
 * de ler como defeito: foi exatamente o que aconteceu quando o multi saiu sem cor e
 * sem frase (testado em dev, 2026-07-28).
 *
 * Componente próprio, e não um bloco de ternários no card: o card já está no teto de
 * complexidade, e estas frases são coesas entre si.
 */
export function LegStatus({
  leg,
  legNumber,
  bestDate,
  analyzed,
  loading,
  copy,
  locale,
}: {
  readonly leg: PlannerLeg;
  readonly legNumber: number;
  readonly bestDate: string | undefined;
  readonly analyzed: number;
  readonly loading: boolean;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
}) {
  const choice = leg.date
    ? copy.stateLeg({ leg: legNumber, date: fmtWeekdayDayMonth(leg.date, locale) })
    : copy.stateNeedsLegDate({ leg: legNumber });
  const agent = bestDate
    ? {
        best: {
          day: copy.bestDayLeg({
            leg: legNumber,
            date: fmtWeekdayDayMonth(bestDate, locale),
          }),
        },
        meta: copy.analyzed({ count: analyzed }),
      }
    : { notice: copy.noPriceTrend };
  return (
    <PlannerStatus choice={choice} loading={loading} loadingLabel={copy.loadingPrices} {...agent} />
  );
}
